import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import notificationService from '../services/notificationService';
import { getAppointments } from '../services/appointmentApi';

interface AppointmentReminder {
  appointmentId: string;
  clientName: string;
  scheduledDate: Date;
  reminderSent: boolean;
}

/**
 * Hook para gerenciar notificações em tempo real e lembretes automáticos
 */
export const useRealtimeNotifications = () => {
  const { user } = useAuth();
  const reminderCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkedRemindersRef = useRef<Set<string>>(new Set());

  /**
   * Verificar e enviar lembretes 24h antes dos agendamentos
   */
  const checkAndSendReminders = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Buscar agendamentos dos próximos 2 dias
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const dayAfterTomorrow = new Date(tomorrow);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
      dayAfterTomorrow.setHours(23, 59, 59, 999);

      const appointments = await getAppointments({
        start_date: tomorrow.toISOString().split('T')[0],
        end_date: dayAfterTomorrow.toISOString().split('T')[0],
      });

      if (!appointments) {
        return; // Não há agendamentos ou erro na requisição
      }

      const appointmentsArray = Array.isArray(appointments)
        ? appointments
        : (appointments?.appointments || appointments?.data || []);

      const now = new Date();
      const tomorrow24h = new Date(tomorrow);
      tomorrow24h.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

      // Filtrar agendamentos que estão exatamente 24h antes
      const remindersToSend = appointmentsArray
        .filter((apt: any) => {
          const aptDate = new Date(apt.scheduled_date);
          const timeDiff = aptDate.getTime() - now.getTime();
          const hoursDiff = timeDiff / (1000 * 60 * 60);

          // Entre 23.5h e 24.5h antes (janela de 1 hora)
          return hoursDiff >= 23.5 && hoursDiff <= 24.5;
        })
        .filter((apt: any) => {
          // Não enviar se já foi enviado
          const reminderKey = `reminder-${apt.id}-${new Date(apt.scheduled_date).toISOString().split('T')[0]}`;
          return !checkedRemindersRef.current.has(reminderKey);
        });

      // Enviar lembretes
      for (const apt of remindersToSend) {
        const reminderKey = `reminder-${apt.id}-${new Date(apt.scheduled_date).toISOString().split('T')[0]}`;

        try {
          // Criar notificação de lembrete
          const reminderNotification = {
            title: 'Lembrete de Agendamento',
            body: `Você tem um agendamento amanhã às ${new Date(apt.scheduled_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} com ${apt.client_name || 'cliente'}`,
            icon: '/favicon.ico',
            tag: `reminder-${apt.id}`,
            data: {
              type: 'appointment_reminder',
              appointmentId: apt.id,
              scheduledDate: apt.scheduled_date,
            },
          };

          // Enviar notificação push se o usuário estiver inscrito
          // Verificar se SW é suportado antes de tentar obter subscription
          let subscription = null;
          try {
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
              subscription = await notificationService.getSubscription();
            }
          } catch (e) {
            console.debug('Service Worker not available for push notifications');
          }

          if (subscription) {
            // Notificação será enviada pelo backend quando o agendamento for criado/atualizado
            // Aqui apenas marcamos como verificado
            checkedRemindersRef.current.add(reminderKey);
          } else {
            // Se não há subscription, mostrar notificação local
            if (Notification.permission === 'granted') {
              await notificationService.showLocalNotification(reminderNotification as any);
              checkedRemindersRef.current.add(reminderKey);
            }
          }
        } catch (error) {
          console.error('Erro ao enviar lembrete:', error);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar lembretes:', error);
    }
  }, [user?.id]);

  /**
   * Inicializar verificação periódica de lembretes
   */
  useEffect(() => {
    if (!user?.id) return;

    // Verificar imediatamente
    checkAndSendReminders();

    // Verificar a cada hora
    reminderCheckIntervalRef.current = setInterval(() => {
      checkAndSendReminders();
    }, 60 * 60 * 1000); // 1 hora

    return () => {
      if (reminderCheckIntervalRef.current) {
        clearInterval(reminderCheckIntervalRef.current);
      }
    };
  }, [user?.id, checkAndSendReminders]);

  /**
   * Limpar lembretes antigos (mais de 2 dias)
   */
  useEffect(() => {
    const cleanupOldReminders = () => {
      const twoDaysAgo = Date.now() - (2 * 24 * 60 * 60 * 1000);
      const keysToRemove: string[] = [];

      checkedRemindersRef.current.forEach((key) => {
        // Extrair timestamp do key se possível
        // Por enquanto, apenas limpar todos os dias
        if (key.includes('reminder-')) {
          keysToRemove.push(key);
        }
      });

      keysToRemove.forEach((key) => {
        checkedRemindersRef.current.delete(key);
      });
    };

    // Limpar a cada 6 horas
    const cleanupInterval = setInterval(cleanupOldReminders, 6 * 60 * 60 * 1000);

    return () => {
      clearInterval(cleanupInterval);
    };
  }, []);

  return {
    checkAndSendReminders,
  };
};

